/*
 * Sidebar navigation — the single source of truth for the menu.
 * Ported from html-markup/assets/js/shell.js NAV.
 *
 * `roles` gates a group: the Platform group only renders for superAdmin, which
 * is what DashboardLayout in the Next.js app did by conditionally unshifting the
 * entry. Declaring it here instead keeps the gate visible and testable.
 *
 * `to` values match the current app's routes exactly. Bookmarks and any deep
 * link the desktop app opens must keep working.
 */
export const NAV_GROUPS = [
    {
        key: "platform",
        label: "Platform",
        note: "super admin only",
        roles: ["superAdmin"],
        /*
         * App Versions is its own destination rather than a tab inside Super
         * Admin. Everything under /super-admin is about ONE organization at a
         * time — its package, its seats, its users — while a published desktop
         * build is platform-wide and every organization's agents update from
         * it. Sitting it beside the per-organization tabs implied a scope it
         * does not have.
         */
        items: [
            { key: "super-admin", label: "Super Admin", to: "/super-admin", icon: "shield" },
            { key: "app-versions", label: "App Versions", to: "/app-versions", icon: "download" },
        ],
    },
    /*
     * Overview and Organization are for people who HAVE an organization.
     *
     * A superAdmin does not: create-superadmin.js leaves User.organization
     * unset deliberately, and the live account has none. Every page in these
     * two groups resolves req.user.organization, so for a superAdmin they are
     * dead ends — /screenshots answers 400 "User organization not found",
     * /organization has no id to query and sits on "Loading…" forever, and
     * /tracking-schedule is requireRole(["admin","manager"]), which does not
     * include superAdmin.
     *
     * If superAdmins are ever given an organization, this is the one line to
     * change.
     */
    {
        key: "overview",
        label: "Overview",
        roles: ["admin", "manager", "employee"],
        items: [
            { key: "dashboard", label: "Dashboard", to: "/dashboard", icon: "grid" },
            { key: "screenshots", label: "Screenshots", to: "/screenshots", icon: "camera" },
            { key: "reports", label: "Reports", to: "/reports", icon: "chart" },
        ],
    },
    {
        key: "organization",
        label: "Organization",
        roles: ["admin", "manager", "employee"],
        items: [
            { key: "teams", label: "Teams", to: "/teams", icon: "team" },
            { key: "projects", label: "Projects", to: "/projects", icon: "folder" },
            {
                key: "productivity-rules",
                label: "Productivity Rules",
                to: "/productivity-rules",
                icon: "squares",
            },
            // `badge` is filled at render time from the users query, so the count
            // is real rather than the hard-coded 18 the markup showed.
            { key: "users", label: "Users", to: "/users", icon: "user", badge: "userCount" },
            { key: "tracking-schedule", label: "Schedules", to: "/tracking-schedule", icon: "calendar" },
            { key: "organization", label: "Settings", to: "/organization", icon: "cog" },
        ],
    },
];

export const FOOTER_NAV = [
    // Org-only: the install command for THIS org (ORG=<identifier> baked in) so any
    // machine can install + auto-register to it. `roles` hides it from a superAdmin
    // (no organization); the Sidebar footer filters on it.
    {
        key: "download-app",
        label: "Download App",
        to: "/download-app",
        icon: "download",
        roles: ["admin", "manager", "employee"],
    },
    { key: "documentation", label: "Help & Docs", to: "/documentation", icon: "book" },
];

/** Groups this role may see. */
export function navGroupsFor(role) {
    return NAV_GROUPS.filter((g) => !g.roles || g.roles.includes(role));
}
